// form
import { useI18n } from 'iomart-hooks';
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import { Box, TextField, TextFieldProps, Typography } from '@mui/material';
import { useState, useCallback } from 'react';

// ----------------------------------------------------------------------

type Props = TextFieldProps & {
  name: string;
  showError?: boolean;
};

export default function RHFJsonEditor({ 
  name, 
  label, 
  helperText, 
  showError = true,
  rows = 6,
  ...other 
}: Props) {
  const { control } = useFormContext();
  const { t } = useI18n(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Функция для расчета количества строк
  const calculateRows = useCallback((value: string) => {
    const minRows = Number(rows);
    if (!value) return minRows;
    const lineCount = value.split('\n').length;
    return Math.max(lineCount + 1, minRows); // Минимум rows, максимум lineCount + 1
  }, [rows]);

  const validateJson = useCallback((value: string) => {
    if (!value || value.trim() === '') {
      setJsonError(null);
      return true;
    }
    
    try {
      JSON.parse(value);
      setJsonError(null);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Invalid JSON';
      setJsonError(errorMessage);
      return false;
    }
  }, []);

  const formatJson = useCallback((value: string) => {
    if (!value || value.trim() === '') {
      return '';
    }
    
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  }, []);

  const handleBlur = useCallback((onBlur: () => void, value: string) => {
    validateJson(value);
    onBlur();
  }, [validateJson]);

  const handleChange = useCallback((onChange: (value: string) => void, value: string) => {
    validateJson(value);
    onChange(value);
  }, [validateJson]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box>
          <TextField
            {...field}
            label={t(label)}
            multiline
            rows={calculateRows(field.value || '')}
            sx={{
              '& .MuiOutlinedInput-input': {
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                fontSize: '13px',
                lineHeight: 1.5,
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'text.disabled',
                opacity: 0.7,
              },
              '& .Mui-disabled .MuiInputBase-input::placeholder': {
                color: 'text.disabled',
                opacity: 0.5,
              },
            }}
            fullWidth
            value={field.value || ''}
            error={!!error || !!jsonError}
            helperText={
              error?.message || 
              jsonError || 
              helperText
            }
            size="small"
            InputLabelProps={{ shrink: true }}
            placeholder='{"key": "value"}'
            onBlur={(e) => handleBlur(field.onBlur, e.target.value)}
            onChange={(e) => handleChange(field.onChange, e.target.value)}
            {...other}
          />
          
          {!other.disabled && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              {field.value && !jsonError && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': { color: 'primary.main' }
                  }}
                  onClick={() => {
                    const formatted = formatJson(field.value);
                    field.onChange(formatted);
                  }}
                >
                  Форматировать JSON
                </Typography>
              )}
              
              {!field.value && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': { color: 'primary.main' }
                  }}
                  onClick={() => {
                    const template = other.placeholder || '{"key": "value"}';
                    field.onChange(template);
                  }}
                >
                  Вставить шаблон
                </Typography>
              )}
            </Box>
          )}
          

        </Box>
      )}
    />
  );
} 